import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createBlock as _createBlock, Fragment as _Fragment, renderList as _renderList } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-4 pt-3 font-catering-body" }
const _hoisted_2 = {
  key: 0,
  class: "m-0 -mt-1 text-xs leading-relaxed text-catering-ink-500"
}
const _hoisted_3 = {
  key: 1,
  class: "rounded-catering-md border border-catering-cream-400 bg-catering-cream-200 p-4"
}
const _hoisted_4 = { class: "flex items-center gap-3" }
const _hoisted_5 = { class: "min-w-0 flex-1" }
const _hoisted_6 = ["href"]
const _hoisted_7 = {
  key: 1,
  class: "block truncate font-catering-mono text-base text-catering-ink-500"
}
const _hoisted_8 = {
  key: 1,
  class: "flex items-center gap-2 text-xs font-semibold text-catering-ink-500"
}
const _hoisted_9 = {
  key: 0,
  class: "m-0 mt-3 text-xs leading-relaxed text-catering-ink-500"
}
const _hoisted_10 = { class: "rounded-catering-md border border-catering-cream-400 bg-catering-cream-200 p-4" }
const _hoisted_11 = { class: "flex items-center gap-3" }
const _hoisted_12 = { class: "min-w-0 flex-1" }
const _hoisted_13 = { class: "block font-catering-mono text-base text-catering-ink-900" }
const _hoisted_14 = {
  key: 0,
  class: "mb-0 mt-3 text-sm leading-relaxed text-catering-ink-600"
}
const _hoisted_15 = { class: "font-catering-mono text-catering-ink-900" }
const _hoisted_16 = { class: "font-catering-mono text-catering-ink-900" }
const _hoisted_17 = {
  key: 1,
  class: "mb-0 mt-3 text-sm leading-relaxed text-catering-ink-600"
}
const _hoisted_18 = {
  key: 2,
  class: "mt-3 rounded-catering-sm bg-catering-cream-300 p-3"
}
const _hoisted_19 = { class: "m-0 text-sm leading-relaxed text-catering-ink-600" }
const _hoisted_20 = {
  key: 4,
  class: "mt-3 flex flex-col gap-3"
}
const _hoisted_21 = { class: "flex items-end gap-2" }
const _hoisted_22 = {
  key: 0,
  class: "m-0 text-sm text-catering-berry-600"
}
const _hoisted_23 = { class: "m-0 text-sm text-catering-ink-600" }
const _hoisted_24 = {
  key: 0,
  class: "grid grid-cols-1 gap-2.5 sm:grid-cols-2"
}
const _hoisted_25 = { class: "font-catering-mono text-catering-ink-900" }
const _hoisted_26 = {
  key: 0,
  class: "text-sm text-catering-ink-500"
}
const _hoisted_27 = {
  key: 1,
  class: "m-0 text-sm text-catering-ink-500"
}
const _hoisted_28 = {
  key: 2,
  class: "m-0 text-sm text-catering-berry-600"
}
const _hoisted_29 = { class: "flex items-center gap-3" }
const _hoisted_30 = { class: "flex justify-end pt-1" }

import { BuildingOffice2Icon, GlobeAltIcon, PhoneIcon } from '@heroicons/vue/24/outline'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

import CateringField from '@/components/catering/CateringField.vue'
import GlowFrame from '@/components/catering/GlowFrame.vue'
import OptionCard from '@/components/catering/OptionCard.vue'
import Pill from '@/components/catering/Pill.vue'
import SwooshArrow from '@/components/catering/SwooshArrow.vue'
import NeueButton from '@/components/neue/NeueButton.vue'
import { areaCodeErrorMessage, isCheckableAreaCode } from '@/components/setup/steps/areaCodeError'
import { companyNameAccepted, companyNameError } from '@/components/setup/steps/companyNameCheck'
import { storeDetailsAction } from '@/components/setup/steps/storeDetailsAction'
import { isLinkable as linkableState, MAX_POLL_MS, normalizeState, POLL_MS } from '@/composables/domainStatus'
import { areaCodeMismatch, useSetupState } from '@/composables/setupState'
import api from '@/store/api'

const SPENT_REASON = 'You have changed this number once already. Message us and we will change it for you.'
const CONVERSATIONS_REASON =
  'This number is already carrying conversations. Swapping it would leave those threads behind, so it stays as it is from here on.'

// A merchant who has spent their change is told so on sight rather than after
// filling in an area code and being refused.

export default {
  __name: 'SetupStoreDetailsStep',
  props: {
  data: { type: Object, default: () => ({}) }
},
  emits: ['done'],
  setup(__props, { emit: __emit }) {

const props = __props
const emit = __emit

const { applyStep, refresh } = useSetupState((...args) => api.call(...args))

const CALL_OPTIONS = { receive: false, redirectOnForbidden: false }

// The PATCH is the confirmation, so the step it answers with is the only thing
// that says whether this is done. Merging it is what moves the badge, the bar and
// the accordion together without going back to the server for the whole state.
const saveStoreDetails = async companyName => {
  const response = await api.call('PATCH', 'setup/store_details', { company_name: companyName }, CALL_OPTIONS)
  applyStep(response.data)
}

const lookupAreaCode = async code => {
  const response = await api.call('GET', `setup/area_code?area_code=${encodeURIComponent(code)}`, {}, CALL_OPTIONS)
  return response.data
}

const swapAreaCode = async code => {
  await api.call('POST', 'setup/area_code', { area_code: code }, CALL_OPTIONS)
  await refresh()
}

const companyName = ref(props.data.company_name || '')
const saving = ref(false)
const saveError = ref('')

const checkingName = ref(false)
const nameNetError = ref(false)
const nameResult = ref(null)

// A rename moves the storefront, so the merchant is looking at a hostname that
// may be minutes from resolving. The link is held back until it does: clicking a
// propagating domain lands on a certificate warning, which reads as a broken
// store rather than one that is not ready yet.
const storefrontHost = ref('')
const storefrontUrl = ref('')
const storefrontState = ref('')
const storefrontLinkable = computed(() => linkableState(storefrontState.value))

let domainTimer = null
let domainElapsed = 0

const stopPolling = () => {
  if (domainTimer) clearTimeout(domainTimer)
  domainTimer = null
}

// A failed check leaves the row hidden rather than replacing it with an error:
// the storefront is not what the merchant came to this step for, and a store
// that works must not be reported as broken by a courtesy poll.
const checkDomain = async () => {
  try {
    const response = await api.call('GET', 'setup/domain_status', {}, CALL_OPTIONS)
    const payload = response?.data || {}
    storefrontHost.value = payload.host || ''
    storefrontUrl.value = payload.url || (payload.host ? `https://${payload.host}` : '')
    storefrontState.value = normalizeState(payload)
  } catch {
    stopPolling()
    return
  }

  if (storefrontLinkable.value || domainElapsed >= MAX_POLL_MS) return stopPolling()

  domainElapsed += POLL_MS
  domainTimer = setTimeout(checkDomain, POLL_MS)
}

const checkable = computed(() => isCheckableAreaCode(codeInput.value))

const nameLocked = computed(() => props.data.company_name_changeable === false)
const numberLocked = computed(() => props.data.phone_number_changeable === false)
const addedByTeam = computed(() => props.data.phone_number_added_by_team === true)

const lockedBySwap = ref(false)
const lockedReason = computed(() => (numberLocked.value || lockedBySwap.value ? SPENT_REASON : CONVERSATIONS_REASON))

const panel = ref(numberLocked.value ? 'locked' : 'closed')
const codeInput = ref('')
const checking = ref(false)
const lookup = ref(null)
const lookupError = ref('')
const selected = ref('')
const swapping = ref(false)
const swapError = ref('')

const phoneNumber = computed(() => props.data.phone_number || '')
const action = computed(() => storeDetailsAction(companyName.value, props.data.company_name))
const mismatch = computed(() => areaCodeMismatch(props.data))
const options = computed(() => {
  if (!lookup.value) return []
  const head = lookup.value.available ? [{ area_code: lookup.value.areaCode, area_city: lookup.value.areaCity }] : []
  return [...head, ...lookup.value.suggestions]
})

watch(
  () => props.data.company_name,
  (value, previous) => {
    if (companyName.value === (previous || '')) companyName.value = value || ''
  }
)

let checkSeq = 0

// The field carries the rejection in red, so leaving it there while the merchant
// types the fix would mark a name the server has not seen yet as refused. The
// verdict is dropped for the same reason: it belongs to the name that was
// checked, not to the one now in the box.
watch(companyName, () => {
  saveError.value = ''
  checkSeq += 1
  nameResult.value = null
  nameNetError.value = false
  checkingName.value = false
})

// The same validation the onboarding journey runs, against the handle the name
// would produce. The merchant already owns theirs and the drawer never rewrites
// it, so the server leaves them out of the clash check.
const checkName = async () => {
  const typed = companyName.value.trim()
  const seq = (checkSeq += 1)
  nameResult.value = null
  nameNetError.value = false
  if (!typed) return
  checkingName.value = true
  try {
    const response = await api.call('POST', 'setup/check_handle', { company_name: typed }, CALL_OPTIONS)
    if (seq === checkSeq) nameResult.value = response.data
  } catch {
    if (seq === checkSeq) nameNetError.value = true
  } finally {
    if (seq === checkSeq) checkingName.value = false
  }
}

const nameError = computed(() => companyNameError({ result: nameResult.value, netError: nameNetError.value }))
const nameOk = computed(() => companyNameAccepted(nameResult.value))
const nameHint = computed(() => {
  if (checkingName.value) return 'Checking that name...'
  if (!action.value.submittable) return 'Add the name customers should see, then save it to finish this step.'
  return ''
})
const canSubmit = computed(() => action.value.submittable && nameOk.value && !checkingName.value)

// A merchant who changed nothing still has to get past the check to confirm, so
// the name they arrived with is checked before they touch anything.
onMounted(checkName)

const messageFrom = (error, fallback) => {
  const errors = error?.response?.data?.errors
  return Array.isArray(errors) && errors.length ? errors.join('. ') : fallback
}

const normalizeLookup = (payload, areaCode) => {
  const raw = payload?.suggestions || payload?.nearby || payload?.candidates || []
  const suggestions = raw
    .map(entry =>
      typeof entry === 'string'
        ? { area_code: entry, area_city: '' }
        : { area_code: String(entry.area_code ?? entry.code ?? ''), area_city: entry.area_city || entry.city || '' }
    )
    .filter(entry => entry.area_code && entry.area_code !== areaCode)
  return {
    areaCode: String(payload?.area_code ?? areaCode),
    areaCity: payload?.area_city || payload?.city || '',
    available: !!(payload?.available ?? payload?.availability),
    suggestions
  }
}

const openPanel = () => {
  panel.value = 'open'
  codeInput.value = mismatch.value?.requested || ''
  lookup.value = null
  lookupError.value = ''
  swapError.value = ''
  selected.value = ''
}

const closePanel = () => {
  panel.value = 'closed'
  lookup.value = null
  selected.value = ''
}

const check = async () => {
  if (checking.value || codeInput.value.length !== 3) return
  checking.value = true
  lookupError.value = ''
  swapError.value = ''
  lookup.value = null
  selected.value = ''
  try {
    const payload = await lookupAreaCode(codeInput.value)
    lookup.value = normalizeLookup(payload, codeInput.value)
    if (lookup.value.available) selected.value = lookup.value.areaCode
  } catch (error) {
    if (error?.response?.status === 409) lockPanel(error)
    else
      lookupError.value = areaCodeErrorMessage(
        error,
        'We could not check that area code just now. Try again in a moment.'
      )
  } finally {
    checking.value = false
  }
}

onMounted(checkDomain)
onUnmounted(stopPolling)

const lockPanel = error => {
  lockedBySwap.value = error?.response?.data?.error === 'already_swapped'
  panel.value = 'locked'
}

const swap = async () => {
  if (swapping.value || !selected.value) return
  swapping.value = true
  swapError.value = ''
  try {
    await swapAreaCode(selected.value)
    closePanel()
  } catch (error) {
    if (error?.response?.status === 409) lockPanel(error)
    else swapError.value = areaCodeErrorMessage(error, 'That swap did not go through. Try again in a moment.')
  } finally {
    swapping.value = false
  }
}

const save = async () => {
  if (saving.value || !canSubmit.value) return
  saving.value = true
  saveError.value = ''
  try {
    await saveStoreDetails(companyName.value.trim())
    emit('done')
  } catch (error) {
    saveError.value = messageFrom(error, 'We could not save that name. Try again in a moment.')
  } finally {
    saving.value = false
  }
}

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[14] || (_cache[14] = _createElementVNode("p", { class: "m-0 text-sm leading-relaxed text-catering-ink-600" }, " The name customers see at checkout and on every text you send. ", -1)),
    _createVNode(CateringField, {
      modelValue: companyName.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => ((companyName).value = $event)),
      label: "Company name",
      placeholder: "Golden Fork Catering",
      required: "",
      disabled: nameLocked.value,
      error: saveError.value || nameError.value,
      hint: nameHint.value,
      onBlur: checkName
    }, {
      leftIcon: _withCtx(() => [
        _createVNode(_unref(BuildingOffice2Icon), { class: "h-[18px] w-[18px]" })
      ]),
      _: 1
    }, 8, ["modelValue", "disabled", "error", "hint"]),
    (nameLocked.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_2, " Your store address moved with this name, so it is a one-time change. Message us and we will change it for you. "))
      : _createCommentVNode("", true),
    (storefrontHost.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_unref(GlobeAltIcon), {
              class: "h-[18px] w-[18px] flex-shrink-0 text-catering-ink-500",
              "aria-hidden": "true"
            }),
            _createElementVNode("div", _hoisted_5, [
              _cache[2] || (_cache[2] = _createElementVNode("span", { class: "block text-xs font-semibold text-catering-ink-500" }, "Your store address", -1)),
              (storefrontLinkable.value)
                ? (_openBlock(), _createElementBlock("a", {
                    key: 0,
                    href: storefrontUrl.value,
                    target: "_blank",
                    rel: "noopener noreferrer",
                    class: "block truncate font-catering-mono text-base text-catering-ink-900 hover:underline"
                  }, _toDisplayString(storefrontHost.value), 9, _hoisted_6))
                : (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(storefrontHost.value), 1))
            ]),
            (storefrontLinkable.value)
              ? (_openBlock(), _createBlock(Pill, {
                  key: 0,
                  tone: "success",
                  size: "sm"
                }, {
                  default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                    _createTextVNode("Live", -1)
                  ]))]),
                  _: 1
                }))
              : (_openBlock(), _createElementBlock("span", _hoisted_8, [...(_cache[4] || (_cache[4] = [
                  _createElementVNode("span", { class: "h-[13px] w-[13px] animate-spin rounded-full border-2 border-catering-ink-400 border-t-transparent" }, null, -1),
                  _createTextVNode(" Going live ", -1)
                ]))]))
          ]),
          (!storefrontLinkable.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_9, " Your address is still reaching the rest of the internet. It usually takes a few minutes, and nothing else is waiting on it. "))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_10, [
      _createElementVNode("div", _hoisted_11, [
        _createVNode(_unref(PhoneIcon), {
          class: "h-[18px] w-[18px] flex-shrink-0 text-catering-ink-500",
          "aria-hidden": "true"
        }),
        _createElementVNode("div", _hoisted_12, [
          _cache[5] || (_cache[5] = _createElementVNode("span", { class: "block text-xs font-semibold text-catering-ink-500" }, "Your Bottle number", -1)),
          _createElementVNode("span", _hoisted_13, _toDisplayString(phoneNumber.value || (addedByTeam.value ? 'Our team is adding your number' : 'Being provisioned')), 1)
        ]),
        _createVNode(Pill, {
          tone: "neutral",
          size: "sm"
        }, {
          default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
            _createTextVNode("Read only", -1)
          ]))]),
          _: 1
        })
      ]),
      (mismatch.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_14, [
            _cache[9] || (_cache[9] = _createTextVNode(" You asked for a ", -1)),
            _createElementVNode("span", _hoisted_15, "(" + _toDisplayString(mismatch.value.requested) + ")", 1),
            _cache[10] || (_cache[10] = _createTextVNode(" number and nothing was free in that area code. ", -1)),
            (mismatch.value.provisioned)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _cache[7] || (_cache[7] = _createTextVNode(" The number above is a ", -1)),
                  _createElementVNode("span", _hoisted_16, "(" + _toDisplayString(mismatch.value.provisioned) + ")", 1),
                  _cache[8] || (_cache[8] = _createTextVNode(" one instead. ", -1))
                ], 64))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createTextVNode("The number above came from somewhere else.")
                ], 64))
          ]))
        : _createCommentVNode("", true),
      (addedByTeam.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_17, " Numbers outside the US and Canada are added by our team after signup, so there is no area code to pick here. "))
        : (panel.value === 'locked')
          ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
              _createElementVNode("p", _hoisted_19, _toDisplayString(lockedReason.value), 1)
            ]))
          : (panel.value === 'closed')
            ? (_openBlock(), _createElementBlock("button", {
                key: 3,
                type: "button",
                class: "mt-3 cursor-pointer border-none bg-transparent p-0 text-sm font-semibold text-catering-tomato-600 underline underline-offset-2",
                onClick: openPanel
              }, " Request a different area code "))
            : (_openBlock(), _createElementBlock("div", _hoisted_20, [
                _createElementVNode("div", _hoisted_21, [
                  _createVNode(CateringField, {
                    modelValue: codeInput.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => ((codeInput).value = $event)),
                    label: "Area code",
                    placeholder: "512",
                    restrict: "integer",
                    maxlength: "3"
                  }, null, 8, ["modelValue"]),
                  _createVNode(NeueButton, {
                    type: "cream",
                    loading: checking.value,
                    disabled: !checkable.value,
                    onClick: check
                  }, {
                    default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                      _createTextVNode("Check", -1)
                    ]))]),
                    _: 1
                  }, 8, ["loading", "disabled"])
                ]),
                (lookupError.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_22, _toDisplayString(lookupError.value), 1))
                  : _createCommentVNode("", true),
                (lookup.value)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createElementVNode("p", _hoisted_23, _toDisplayString(lookup.value.available
                ? `(${lookup.value.areaCode}) has numbers free.`
                : `(${lookup.value.areaCode}) has nothing free right now.`), 1),
                      (options.value.length)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(options.value, (option) => {
                              return (_openBlock(), _createBlock(OptionCard, {
                                key: option.area_code,
                                sel: "radio",
                                selected: selected.value === option.area_code,
                                onClick: $event => (selected.value = option.area_code)
                              }, {
                                default: _withCtx(() => [
                                  _createElementVNode("span", _hoisted_25, "(" + _toDisplayString(option.area_code) + ")", 1),
                                  (option.area_city)
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_26, _toDisplayString(option.area_city), 1))
                                    : _createCommentVNode("", true)
                                ]),
                                _: 2
                              }, 1032, ["selected", "onClick"]))
                            }), 128))
                          ]))
                        : (_openBlock(), _createElementBlock("p", _hoisted_27, "Nothing nearby is free either. Try another area code."))
                    ], 64))
                  : _createCommentVNode("", true),
                (swapError.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_28, _toDisplayString(swapError.value), 1))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_29, [
                  _createVNode(NeueButton, {
                    type: selected.value ? 'accent' : 'cream',
                    disabled: !selected.value,
                    loading: swapping.value,
                    onClick: swap
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(selected.value ? `Use (${selected.value})` : 'Pick an area code'), 1)
                    ]),
                    _: 1
                  }, 8, ["type", "disabled", "loading"]),
                  _createVNode(NeueButton, {
                    type: "ghost",
                    size: "sm",
                    onClick: closePanel
                  }, {
                    default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                      _createTextVNode("Cancel", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _cache[13] || (_cache[13] = _createElementVNode("p", { class: "m-0 text-xs leading-relaxed text-catering-ink-500" }, " Swapping releases your current number and buys a new one. It is only possible before customers start texting you. ", -1))
              ]))
    ]),
    _createElementVNode("div", _hoisted_30, [
      _createVNode(GlowFrame, {
        subtle: "",
        "frame-class": "inline-flex rounded-full"
      }, {
        default: _withCtx(() => [
          _createVNode(NeueButton, {
            type: "accent",
            loading: saving.value,
            disabled: !canSubmit.value,
            onClick: save
          }, {
            "icon-right": _withCtx(() => [
              _createVNode(SwooshArrow, { class: "ml-1" })
            ]),
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(action.value.label) + " ", 1)
            ]),
            _: 1
          }, 8, ["loading", "disabled"])
        ]),
        _: 1
      })
    ])
  ]))
}
}

}